import { useCapabilities } from '@/features/authentication/hooks/useCapabilities';
import { NavSectionLabel } from '@/shared/components/AppSidebar/NavSectionLabel';
import { NavigationActivityIndicator } from '@/shared/components/AppSidebar/NavigationActivityIndicator';
import { SidebarMenuButton } from '@/shared/components/AppSidebar/SidebarMenuButton';
import { Button } from '@/shared/components/ui/button';
import { NavEmpty } from '@/shared/components/ui/empty';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  navGroupStackVariants,
  navItemButtonVariants,
  navItemListVariants,
  navItemSkeletonVariants,
  navLabelSkeletonVariants,
  navLabelWrapVariants,
  navScrollVariants,
  permissionNavSkeletonVariants,
} from '@/shared/config/sidebar';
import { useActiveNavHref } from '@/shared/hooks/useActiveNavHref';
import { useNavigationGroups } from '@/shared/hooks/useNavigationItems';
import { useSidebar } from '@/shared/hooks/useSidebar';
import { useNavigationActivityStore } from '@/shared/stores/navigationActivityStore';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { Link } from '@tanstack/react-router';
import { Fragment } from 'react';

/**
 * Loading rows for permission-gated navigation. Unconditional items remain
 * interactive while the V3 authorization availability map is pending.
 */
function PermissionNavSkeleton({ mobile }: { mobile: boolean }) {
  return (
    <div className={permissionNavSkeletonVariants({ mobile })}>
      <Skeleton className={navLabelSkeletonVariants({ mobile })} />
      <Skeleton className={navItemSkeletonVariants({ mobile })} />
      <Skeleton className={navItemSkeletonVariants({ mobile })} />
    </div>
  );
}

/**
 * Default primary-nav body: the permission-gated top-level app navigation.
 * Always mounted in `AppSidebar` (desktop density); also rendered inside
 * {@link MobileNavOverlay} with `mobile` for touch density. Section context (e.g.
 * Raw Footage org lists) mounts beside the section `<Outlet />` in that layout.
 *
 * Unconditional entries render immediately. Permission-gated entries are added
 * after the shared V3 availability query resolves, so a slow or unavailable
 * authorization response never blocks Profile.
 */
export function DefaultNavSidebar({
  onNavigate,
  mobile = false,
}: { onNavigate?: () => void; mobile?: boolean } = {}) {
  const { open } = useSidebar();
  const { i18n } = useLingui();
  const authorization = useCapabilities();
  const groups = useNavigationGroups();
  const activityCounts = useNavigationActivityStore((state) => state.counts);
  const items = groups.flatMap((group) => group.items);
  const activeHref = useActiveNavHref(items);
  const showSectionLabels = open || mobile;

  return (
    <div className={navScrollVariants({ mobile })}>
      <div className={navGroupStackVariants({ mobile })}>
        {groups.map((group, index) => (
          <Fragment key={group.id}>
            {/* Collapsed icon rail: a rule between groups stands in for section labels.
                Use border-t (not h-px + bg) so the rule stays crisp on retina. */}
            {!showSectionLabels && index > 0 && (
              <div className="mx-0.5 border-sidebar-border border-t" />
            )}
            <div>
              {showSectionLabels && (
                <div className={navLabelWrapVariants({ mobile })}>
                  <NavSectionLabel size={mobile ? 'comfortable' : 'compact'}>
                    {group.label}
                  </NavSectionLabel>
                </div>
              )}
              <ul className={navItemListVariants({ mobile })}>
                {group.items.map((item) => {
                  const activityCount = item.activityId
                    ? (activityCounts[item.activityId] ?? 0)
                    : 0;
                  const desktopDestination =
                    typeof item.destination === 'function'
                      ? (item.destination(authorization.hasCapability) ?? item.to)
                      : (item.destination ?? item.to);
                  const destination = mobile ? item.to : desktopDestination;
                  return (
                    <li key={item.to} className="group/menu-item relative">
                      <SidebarMenuButton
                        asChild
                        isActive={item.to === activeHref}
                        size={mobile ? 'lg' : 'sm'}
                        mobile={mobile}
                        className={navItemButtonVariants({ mobile })}
                        tooltip={item.tooltip ? i18n._(item.tooltip) : undefined}
                      >
                        <Link to={destination} onClick={onNavigate}>
                          {item.icon}
                          <span>{item.label}</span>
                          {activityCount > 0 ? (
                            <NavigationActivityIndicator
                              count={activityCount}
                              expanded={showSectionLabels}
                            />
                          ) : null}
                        </Link>
                      </SidebarMenuButton>
                    </li>
                  );
                })}
              </ul>
            </div>
          </Fragment>
        ))}
        {authorization.isPending ? <PermissionNavSkeleton mobile={mobile} /> : null}
        {authorization.isError ? (
          <div className="flex flex-col items-center gap-2 px-2 pt-3">
            <NavEmpty>
              <Trans>Couldn’t load the rest of the sidebar.</Trans>
            </NavEmpty>
            <Button variant="outline" size="sm" onClick={authorization.refetch}>
              <Trans>Try again</Trans>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  );
}
